<template>
    <div class="dg-page">
        <div class="dg-header">
            <div class="dg-header-left">
                <div class="dg-icon-wrap"><i class="fas fa-id-badge"></i></div>
                <h2 class="dg-title">Designations</h2>
                <span class="dg-count" v-if="items.length">{{ items.length }}</span>
            </div>
            <button class="dg-add-btn" @click="showForm = true">
                <i class="fas fa-plus"></i>
                Add Designation
            </button>
        </div>

        <RightSideModal :show="showForm" :title="editMode ? 'Edit Designation' : 'New Designation'" @close="closeForm">
            <Form :fields="createFields" title="Designation" submitUrl="/designations" updateRootUrl="/designations"
                @success="handleFormSuccess" @cancel="closeForm" :editMode="editMode" :editData="editData" />
        </RightSideModal>

        <div v-if="loading" class="dg-loading"><div class="dg-spinner"></div><span>Loading...</span></div>
        <div v-else-if="!items.length" class="dg-empty">
            <div class="dg-empty-icon"><i class="fas fa-id-badge"></i></div>
            <h3>No designations found</h3>
        </div>
        <div v-else class="dg-grid">
            <div class="dg-card" v-for="(item, idx) in items" :key="item.id" :style="{ animationDelay: `${idx * 0.03}s` }">
                <div class="dg-card-top">
                    <div class="dg-card-icon" :class="dgColor(idx)"><i class="fas fa-id-badge"></i></div>
                    <div class="dg-card-info">
                        <h4 class="dg-card-name">{{ item.name }}</h4>
                        <span class="dg-card-dept" v-if="item.department">
                            <i class="fas fa-sitemap"></i> {{ item.department.name }}
                        </span>
                    </div>
                </div>
                <div class="dg-card-meta" v-if="item.noticePeriodDays">
                    <span class="dg-meta-label">Notice Period</span>
                    <span class="dg-meta-val">{{ item.noticePeriodDays }} days</span>
                </div>
                <div class="dg-card-actions">
                    <button class="dg-action-btn dg-btn-edit" @click="enableEditing(item)"><i class="fas fa-pen"></i> Edit</button>
                    <button class="dg-action-btn dg-btn-delete" @click="deleteItem(item.id)"><i class="fas fa-trash-alt"></i> Delete</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import Form from "../../components/crud/form.vue"
import RightSideModal from "../../components/layout/RightSideModal.vue"
import { createField } from "../../utils/common"
import { PERMISSION_CODES } from "../../store/permission"

export default {
    data() {
        return {
            PERMISSION_CODES, loading: true, showForm: false, editMode: false, editData: null,
            items: [], departments: [], createFields: [],
        }
    },
    components: { Form, RightSideModal },
    async mounted() {
        await this.fetchDepartments()
        this.createFields = [
            createField("Name*", "name", "text", true),
            createField("Department*", "departmentId", "select", true, "", this.departments.map(d => ({ id: d.id, title: d.name }))),
            createField("Notice Period Days*", "noticePeriodDays", "number", false),
        ]
        await this.fetchData()
        this.loading = false
    },
    methods: {
        dgColor(i) { return ["dg-clr-blue","dg-clr-purple","dg-clr-teal","dg-clr-amber","dg-clr-rose"][i % 5] },
        closeForm() { this.showForm = false; this.editMode = false; this.editData = null },
        handleFormSuccess() { this.closeForm(); this.fetchData() },
        enableEditing(item) { this.editMode = true; this.editData = { ...item }; this.showForm = true; window.scrollTo({ top: 0, behavior: "smooth" }) },
        async fetchDepartments() { try { const r = await http.get("/departments"); this.departments = r.data.result || [] } catch(e) { console.log(e) } },
        async fetchData() { try { const r = await http.get("/designations"); this.items = r.data.result || [] } catch(e) { console.log(e) } },
        async deleteItem(id) {
            if (!confirm("Delete this designation?")) return
            try { await http.delete(`/designations/${id}`); this.$toast.success("Deleted"); this.items = this.items.filter(c => c.id !== id) }
            catch(e) { this.$toast.error("Error deleting") }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.dg-page { animation: dgFade .3s ease; }
@keyframes dgFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.dg-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.dg-header-left { display:flex; align-items:center; gap:10px; }
.dg-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.dg-title { font-size:15px; font-weight:700; color:$gray800; margin:0; }
.dg-count { min-width:20px; height:20px; padding:0 6px; border-radius:$radiusFull; font-size:11px; font-weight:700; background:$primaryColor; color:white; display:inline-flex; align-items:center; justify-content:center; }
.dg-add-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:none; border-radius:6px; background:$primaryColor; color:white; font-size:12.5px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; &:hover { background:$primaryDark; } }
.dg-loading { display:flex; align-items:center; justify-content:center; gap:10px; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; span { font-size:13px; color:$gray400; } }
.dg-spinner { width:24px; height:24px; border:2.5px solid $gray200; border-top-color:$primaryColor; border-radius:50%; animation:dgSpin .7s linear infinite; }
@keyframes dgSpin { to { transform:rotate(360deg); } }
.dg-empty { text-align:center; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; h3 { font-size:14px; font-weight:700; color:$gray600; margin:0; } }
.dg-empty-icon { width:48px; height:48px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; i { font-size:20px; color:$gray300; } }
.dg-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.dg-card { background:white; border:1px solid $gray100; border-radius:10px; padding:14px; box-shadow:$shadowSm; display:flex; flex-direction:column; gap:10px; animation:dgSlide .25s ease both; transition:all $transitionFast; &:hover { border-color:$gray200; box-shadow:$shadowMd; transform:translateY(-1px); } }
@keyframes dgSlide { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.dg-card-top { display:flex; align-items:flex-start; gap:10px; }
.dg-card-icon { width:36px; height:36px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.dg-clr-blue { background:#eff6ff; color:#2563eb; }
.dg-clr-purple { background:#f5f3ff; color:#7c3aed; }
.dg-clr-teal { background:#f0fdfa; color:#0d9488; }
.dg-clr-amber { background:#fffbeb; color:#d97706; }
.dg-clr-rose { background:#fff1f2; color:#e11d48; }
.dg-card-info { flex:1; min-width:0; }
.dg-card-name { font-size:13.5px; font-weight:700; color:$gray800; margin:0 0 3px; }
.dg-card-dept { font-size:11px; color:$gray400; font-weight:500; display:inline-flex; align-items:center; gap:4px; i { font-size:10px; } }
.dg-card-meta { display:flex; align-items:center; justify-content:space-between; background:$gray50; border:1px solid $gray100; border-radius:6px; padding:6px 10px; }
.dg-meta-label { font-size:11px; color:$gray400; font-weight:500; }
.dg-meta-val { font-size:12px; color:$gray700; font-weight:600; }
.dg-card-actions { display:flex; gap:6px; margin-top:auto; padding-top:6px; border-top:1px solid $gray100; }
.dg-action-btn { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; background:white; i { font-size:10px; } }
.dg-btn-edit { color:$primaryColor; &:hover { background:$primaryLight; border-color:rgba(0,94,137,0.15); } }
.dg-btn-delete { color:$danger; &:hover { background:#fef2f2; border-color:rgba(220,38,38,0.15); } }
@media(max-width:992px) { .dg-grid { grid-template-columns:repeat(2,1fr); } }
@media(max-width:480px) { .dg-grid { grid-template-columns:1fr; } }
</style>
